import * as React from "react";
import styled from "styled-components";
import { ColorType } from "@hubper/theme";

import Cell from "./Cell";
import MonthPicker from "./MonthPicker";

import {
  getYear,
  getMonth,
  getDaysInMonth,
  getISODay,
  subDays,
  addDays,
  isWithinRange,
  isSameDay,
  isBefore,
  isPast
} from "date-fns";

const Base = styled.div<{ cellSize: any }>`
  width: calc((${p => p.cellSize} + 1px) * 7);
  user-select: none;
`;

const Cells = styled.div`
  width: 100%;
  width: 224px;
  margin: auto;
  display: flex;
  flex-wrap: wrap;
`;

export type DatePickerProps = {
  cellSize?: string;
  colorType?: ColorType;
  range?: boolean;
  start?: Date | string | null;
  end?: Date | string | null;
  value?: Date | string;
  disableSelectPast?: boolean;
  onChange?: (value: Date | string) => void;
  onRangeChange?: (
    start: Date | string | null,
    end: Date | string | null
  ) => void;
  dayNames: string[];
  monthNames: string[];
  style?: React.CSSProperties;
  className?: string;
};

type State = {
  month: number;
  year: number;
};

class DatePicker extends React.Component<DatePickerProps, State> {
  constructor(props: DatePickerProps) {
    super(props);

    const { start, value } = props;

    if (this.isRange()) {
      this.state = {
        month: getMonth(start || new Date()),
        year: getYear(start || new Date())
      };
    } else {
      this.state = {
        month: getMonth(value || new Date()),
        year: getYear(value || new Date())
      };
    }
  }

  isRange() {
    return Boolean(this.props.range);
  }

  getDatesOfCurrentMonth() {
    const { month, year } = this.state;
    const date = new Date(year, month);

    const daysInMonth = getDaysInMonth(date);

    const startsAtWeekday = getISODay(date);

    const remainingDaysEnd = 35 - daysInMonth - (startsAtWeekday - 1);

    return [
      ...Array(startsAtWeekday - 1)
        .fill(true)
        .map((_, index) => subDays(date, index + 1))
        .reverse(),
      ...Array(daysInMonth + remainingDaysEnd)
        .fill(true)
        .map((_, index) => addDays(date, index))
    ];
  }

  gotoNextMonth = () => {
    const { month, year } = this.state;

    if (month === 11) {
      this.setState({
        year: year + 1,
        month: 0
      });
    } else {
      this.setState({
        month: month + 1
      });
    }
  };

  gotoPreviousMonth = () => {
    const { month, year } = this.state;

    if (month === 0) {
      this.setState({
        year: year - 1,
        month: 11
      });
    } else {
      this.setState({ month: month - 1 });
    }
  };

  handleClick = (date: Date) => {
    const { onChange, onRangeChange, start, end } = this.props;
    if (this.isRange()) {
      if (!start || (start && end)) {
        onRangeChange!(date, null);
        return;
      }

      if (isBefore(date, start)) {
        onRangeChange!(date, start);
        return;
      }

      onRangeChange!(start, date);
      return;
    }

    if (onChange) {
      onChange(date);
    }
  };

  render() {
    const {
      cellSize = "32px",
      value,
      start,
      end,
      colorType = "primary",
      disableSelectPast,
      dayNames,
      monthNames,
      style,
      className
    } = this.props;

    const { month, year } = this.state;

    return (
      <Base style={style} className={className} cellSize={cellSize}>
        <MonthPicker
          year={year}
          month={month}
          onNextMonth={this.gotoNextMonth}
          onPreviousMonth={this.gotoPreviousMonth}
          style={{ marginBottom: "0.66em" }}
          monthNames={monthNames}
        />
        <Cells>
          {dayNames.map(day => (
            <Cell
              disabled
              key={day}
              value={day}
              date={null}
              size={cellSize}
              style={{ fontSize: 12 }}
            />
          ))}
          {this.getDatesOfCurrentMonth().map(date => (
            <Cell
              key={date.toString()}
              date={date}
              colorType={colorType}
              size={cellSize}
              onClick={this.handleClick}
              active={
                (this.isRange() &&
                  ((start && isSameDay(date, start)) ||
                    (end && isSameDay(date, end)))) ||
                ((value && isSameDay(date, value)) as any)
              }
              inRange={
                this.isRange() &&
                start &&
                end &&
                (isWithinRange(date, start, end) as any)
              }
              first={this.isRange() && isSameDay(date, start!)}
              last={this.isRange() && isSameDay(date, end!)}
              disabled={disableSelectPast && isPast(date)}
              dateIsOutsideCurrentMonth={getMonth(date) !== month}
            />
          ))}
        </Cells>
      </Base>
    );
  }
}

export default DatePicker;
